import helperFunctions from '../utils/helperFunctions';
import { provideService } from '../services/provider';

const initialState = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNumber: '',
  birthdate: '',
  streetAddress: '',
  unit: '',
  city: '',
  province: '',
  state: '',
  provinceCode: '',
  stateCode: '',
  postalCode: '',
  zipCode: '',
  country: '',
  // financeApplication object is the finance app data sent to the Create finance application API
  financeApplication: {
    employerName: null,
    employerNumber: null,
    streetNumber: null,
    streetName: null,
    suiteNumber: null,
    city: null,
    province: null,
    provinceCode: null,
    country: null,
    postalCode: null,
    currentOccupation: null,
    yearsWorked: null,
    ownershipType: null,
    mortgageCompany: null,
  },
  // financeDetails object is the finance app details data sent to the Update Lead API
  financeDetails: {
    grossIncome: null,
    incomeDetailsMethod: null,
    annualIncome: null,
    monthlyIncome: null,
    hourlyWage: null,
    hoursPerWeek: null,
    downPayment: null,
    monthlyPayment: null,
    monthlyExpenses: {
      carPayments: null,
      loanPayments: null,
      personalLineOfCredit: null,
      creditCardPayments: null,
    },
  },
  language: '',
  referenceId: '',
  oemOfInterest: null,
  salutation: '',
  homePhoneNumber: '',
};

const getters = {
  getPhoneNumber: state => `+1${state.phoneNumber}`,
  getLanguageCodeForNumericalInternationalization: state => helperFunctions.internationalizationShaper(state.language),
  getFormattedPostalCode: state => (state.postalCode ? helperFunctions.postalCodeShaper(state.postalCode) : null),
};

const mutations = {
  SET_OEM_OF_INTEREST(state, data) {
    state.oemOfInterest = data;
  },
  SET_FIRST_NAME(state, data) {
    state.firstName = data;
  },
  SET_LAST_NAME(state, data) {
    state.lastName = data;
  },
  SET_EMAIL_ADDRESS(state, data) {
    state.emailAddress = data;
  },
  SET_PHONE_NUMBER(state, data) {
    state.phoneNumber = data;
  },
  SET_BIRTHDATE(state, data) {
    state.birthdate = data;
  },
  SET_STREET_ADDRESS(state, data) {
    state.streetAddress = data;
  },
  SET_UNIT(state, data) {
    state.unit = data;
  },
  SET_CITY(state, data) {
    state.city = data;
  },
  SET_PROVINCE(state, data) {
    state.province = data;
    state.country = 'CA';
  },
  SET_STATE(state, data) {
    state.state = data;
    state.country = 'US';
  },
  SET_PROVINCE_CODE(state, data) {
    state.provinceCode = data;
  },
  SET_STATE_CODE(state, data) {
    state.stateCode = data;
  },
  SET_POSTAL_CODE(state, data) {
    state.postalCode = data;
  },
  SET_ZIP_CODE(state, data) {
    state.zipCode = data;
  },
  SET_EMPLOYER_NAME(state, data) {
    state.financeApplication.employerName = data;
  },
  SET_EMPLOYER_NUMBER(state, data) {
    state.financeApplication.employerNumber = data;
  },
  SET_EMPLOYER_STREET_NUMBER(state, data) {
    state.financeApplication.streetNumber = data;
  },
  SET_EMPLOYER_STREET_NAME(state, data) {
    state.financeApplication.streetName = data;
  },
  SET_EMPLOYER_SUIT_NUMBER(state, data) {
    state.financeApplication.suiteNumber = data;
  },
  SET_EMPLOYER_CITY(state, data) {
    state.financeApplication.city = data;
  },
  SET_EMPLOYER_PROVINCE(state, data) {
    state.financeApplication.country = 'ca';
    state.financeApplication.province = data;
  },
  SET_EMPLOYER_PROVINCE_CODE(state, data) {
    state.financeApplication.provinceCode = data;
  },
  SET_EMPLOYER_STATE(state, data) {
    state.financeApplication.country = 'us';
    state.financeApplication.province = data;
  },
  SET_EMPLOYER_STATE_CODE(state, data) {
    state.financeApplication.provinceCode = data;
  },
  SET_EMPLOYER_POSTALCODE(state, data) {
    state.financeApplication.postalCode = data;
  },
  SET_EMPLOYER_ZIPCODE(state, data) {
    state.financeApplication.postalCode = data;
  },
  SET_CURRENT_OCCUPATION(state, data) {
    state.financeApplication.currentOccupation = data;
  },
  SET_YEARS_WORKED(state, data) {
    state.financeApplication.yearsWorked = data;
  },
  SET_OWNERSHIP_TYPE(state, data) {
    state.financeApplication.ownershipType = data || null;
  },
  SET_MORTGAGE_COMPANY(state, data) {
    state.financeApplication.mortgageCompany = data || null;
  },
  SET_REFERENCE_ID(state, data) {
    state.referenceId = data;
  },
  SET_LANGUAGE(state, data) {
    state.language = data;
  },
  UPDATE_USER(state, user) {
    Object.assign(state, user);
  },
  SET_MONTHLY_PAYMENT(state, data) {
    state.financeDetails.monthlyPayment = data || null;
  },
  SET_GROSS_INCOME(state, data) {
    state.financeDetails.grossIncome = data;
  },
  SET_FINANCE_DETAILS_DATA(state, [key, data]) {
    state.financeDetails[key] = data;
  },
  SET_INCOME_DETAILS_METHOD(state, data) {
    state.financeDetails.incomeDetailsMethod = data;
    // Reset non relevant income amount data
    if (data !== 'monthly') {
      state.financeDetails.monthlyIncome = null;
    }
    if (data !== 'annual') {
      state.financeDetails.annualIncome = null;
    }
    if (data !== 'hourly') {
      state.financeDetails.hourlyWage = null;
      state.financeDetails.hoursPerWeek = null;
    }
  },
  SET_SALUTATION(state, data) {
    state.salutation = data;
  },
  SET_HOME_PHONE_NUMBER(state, data) {
    state.homePhoneNumber = data;
  },
  SET_MONTHLY_EXPENSES(state, [key, value]) {
    // Makes sure to only set valid keys
    if (!key || !(key in state.financeDetails.monthlyExpenses)) {
      this._vm.$log(`Error setting monthly expense with key: ${key} and value: ${value}`, new Error());
    } else {
      state.financeDetails.monthlyExpenses[key] = value;
    }
  },
};

const actions = {
  async sendVerificationCode({ commit, state, rootState }, leadId) {
    const body = {
      leadId,
      languageCode: state.language || 'en_CA',
      mobilePhone: state.phoneNumber,
    };
    const isDemo = rootState.business.info.isDemo || process.env.VUE_APP_APP_ENV === 'test';
    const insuranceService = provideService({
      product: 'insurance',
      isDemo,
    });

    const response = await insuranceService.sendVerificationCode(body);
    const referenceId = response.data.id;
    commit('SET_REFERENCE_ID', referenceId);
  },
  async verifyCode({ state, rootState }, code) {
    const body = {
      id: state.referenceId,
      token: code,
    };
    const isDemo = rootState.business.info.isDemo || process.env.VUE_APP_APP_ENV === 'test';
    const insuranceService = provideService({
      product: 'insurance',
      isDemo,
    });

    const res = await insuranceService.verifyCode(body);
    return res;
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  getters,
  actions,
};
